/**
 * LibraryGroupsSection — the "Groups" section inside the Library surface.
 *
 * Lists the caller's groups and offers `New group` (one field: name). Selecting
 * a group routes to `/app/g/<uuid>`. Coexists with the personal Library and
 * Collections (plan §3.3; design.md §6.0a discoverability — Groups also has a
 * labelled user-menu entry, wired in routes/app.tsx).
 *
 * NAV PLACEMENT (resolved 2026-07-02, instrument-design-reviewer): the
 * user-menu "Groups" row lands on the Groups index at /app/g
 * (routes/groups.tsx + GroupsIndexView). This in-library section stays as the
 * in-context surface; both route to the same group pages.
 *
 * Self-fetches via the injected GroupsApi so LibraryView needs no group plumbing.
 * Rule 8: create failures toast; the list-load failure renders a quiet retry.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { isNavigationAbort } from "~/lib/nav-abort";
import type { GroupsApi, GroupSummary } from "./api";
import { GroupNameModal } from "./GroupNameModal";
import { injectGroupStyles } from "./styles";
import { GROUP_READ_ONLY_MARKER } from "./readOnly";

export interface LibraryGroupsSectionProps {
  api: GroupsApi;
  /** Navigate to the group page (`/app/g/<uuid>`). */
  onOpenGroup: (groupUuid: string) => void;
}

type LoadState =
  { kind: "loading" } | { kind: "ready"; groups: GroupSummary[] } | { kind: "error" };

export function LibraryGroupsSection({ api, onOpenGroup }: LibraryGroupsSectionProps) {
  injectGroupStyles();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [showNew, setShowNew] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    setState({ kind: "loading" });
    api
      .list()
      .then((res) => {
        if (mountedRef.current) setState({ kind: "ready", groups: res.groups });
      })
      .catch((err: unknown) => {
        // A nav-abort / post-unmount rejection (route change tearing down the
        // in-flight fetch) is expected noise — debug, not error (GroupPage
        // idiom). A mounted, page-live failure keeps the Rule 8 error + retry.
        if (isNavigationAbort(err, mountedRef.current)) {
          console.debug({ event: "group_list_aborted", err });
          return;
        }
        console.error({ event: "group_list_failed", err });
        setState({ kind: "error" });
      });
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = useCallback(
    async (name: string) => {
      // Rule 8: reject propagates to GroupNameModal's inline error. On success
      // we route to the fresh group so the owner can invite immediately.
      const { uuid } = await api.create({ name });
      setShowNew(false);
      toast.success("Group created.");
      onOpenGroup(uuid);
    },
    [api, onOpenGroup],
  );

  return (
    <section
      className="proof-group-section"
      data-testid="library-groups-section"
      aria-label="Groups"
    >
      <div className="proof-group-section__heading">
        <span style={{ flex: "1 1 auto" }}>Groups</span>
        <button
          type="button"
          className="proof-group-quiet"
          onClick={() => setShowNew(true)}
          data-testid="new-group-btn"
        >
          New group
        </button>
      </div>

      {state.kind === "loading" && (
        <div className="proof-group-loading" aria-busy="true" data-testid="groups-loading">
          Loading…
        </div>
      )}

      {state.kind === "error" && (
        <div className="proof-group-empty" data-testid="groups-load-error">
          Couldn’t load your groups.{" "}
          <button type="button" className="proof-group-quiet" onClick={load}>
            Try again
          </button>
        </div>
      )}

      {state.kind === "ready" && state.groups.length === 0 && (
        <p className="proof-group-empty" data-testid="groups-empty">
          No groups yet. Create one to share recipes with people you cook with.
        </p>
      )}

      {state.kind === "ready" && state.groups.length > 0 && (
        <div className="proof-group-list" data-testid="groups-list">
          {state.groups.map((g) => (
            <button
              key={g.uuid}
              type="button"
              className="proof-group-row"
              onClick={() => onOpenGroup(g.uuid)}
              data-testid="group-row"
            >
              <span className="proof-group-row__text">
                <span className="proof-group-row__name">{g.name}</span>
                <span className="proof-group-row__meta">
                  {" "}
                  <span className="proof-group-mono">{g.memberCount}</span>{" "}
                  {g.memberCount === 1 ? "member" : "members"}
                </span>
              </span>
              {/* Downgrade safety (WP-B): a read-only group is still fully
                  readable, so the row stays a live tap target — the marker just
                  states the group's write state before the user gets there.
                  Text, never an icon (design.md §2). */}
              {g.frozen && (
                <span className="proof-group-row__flag" data-testid="group-row-read-only">
                  {GROUP_READ_ONLY_MARKER}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {showNew && (
        <GroupNameModal
          title="New group"
          submitLabel="Create"
          placeholder="Family"
          onSubmit={handleCreate}
          onClose={() => setShowNew(false)}
        />
      )}
    </section>
  );
}
